/**
 * "Choose a Look" overlay — lets a player pick which of the colour themes, and
 * how large the text, this device shows the app in. Opened by tapping the paint-swatch
 * button branding.ts adds to every screen's header (wired here via a
 * delegated click listener, same reasoning as lightbox.ts: the header is
 * re-rendered by every view, so a listener attached once at startup is more
 * robust than each view remembering to wire its own copy).
 */

import { applyTheme, readTheme, writeTheme, THEMES, type Theme } from './theme';
import { applyTextSize, readTextSize, writeTextSize, TEXT_SIZES, isTextSize } from './textSize';

let overlay: HTMLDivElement | undefined;

function close(): void {
  overlay?.remove();
  overlay = undefined;
  document.removeEventListener('keydown', onKeydown);
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
}

function render(): void {
  if (!overlay) return;
  const current = readTheme();
  const currentSize = readTextSize();
  const panel = overlay.querySelector<HTMLElement>('.modal-panel')!;
  // Text size comes first: it is the setting someone who struggles to read the screen needs, so it
  // must not sit below six colour swatches they would have to scroll past at the largest size.
  panel.innerHTML = `
    <h1>Look &amp; Text Size</h1>
    <h2>Text size</h2>
    <p class="muted">Bigger text and buttons everywhere on this device. Nobody else's screen changes.</p>
    <div class="text-size-row" role="radiogroup" aria-label="Text size">
      ${TEXT_SIZES.map(
        (t) => `
        <button type="button" role="radio" aria-checked="${t.id === currentSize}" class="text-size-btn ${t.id === currentSize ? 'is-current' : ''}" data-text-size="${t.id}">
          <span class="text-size-sample" style="font-size:${t.samplePx}px">Aa</span>
          <span class="text-size-name">${t.name}</span>
        </button>
      `,
      ).join('')}
    </div>

    <h2>Colours</h2>
    <p class="muted">
      Purely how it looks on this device — everyone at the table can pick differently, and it
      never changes how a game scores.
    </p>
    <div class="theme-swatch-grid">
      ${THEMES.map(
        (t) => `
        <button type="button" class="theme-swatch ${t.id === current ? 'is-current' : ''}" data-theme-id="${t.id}">
          <span class="swatch-chips">${t.chips.map((c) => `<span style="background:${c}"></span>`).join('')}</span>
          <span class="swatch-name">${t.name}</span>
          <span class="swatch-desc">${t.description}</span>
        </button>
      `,
      ).join('')}
    </div>
    <button type="button" id="closeThemePicker" class="secondary" style="width:100%;margin-top:1rem">
      Close
    </button>
  `;

  panel.querySelectorAll<HTMLButtonElement>('.theme-swatch').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['themeId'] as Theme;
      writeTheme(id);
      applyTheme(id);
      render(); // re-render so the checkmark/highlight moves without closing the panel
    });
  });
  panel.querySelectorAll<HTMLButtonElement>('.text-size-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['textSize'];
      if (!isTextSize(id)) return;
      writeTextSize(id);
      applyTextSize(id);
      render();
    });
  });
  panel.querySelector('#closeThemePicker')!.addEventListener('click', close);
}

function open(): void {
  close();
  const el = document.createElement('div');
  el.className = 'lightbox-overlay';
  el.innerHTML = `<div class="card modal-panel"></div>`;
  el.addEventListener('click', close);
  el.querySelector('.modal-panel')!.addEventListener('click', (e) => e.stopPropagation());
  document.addEventListener('keydown', onKeydown);
  document.body.appendChild(el);
  overlay = el;
  render();
}

/** Wires the header's paint-swatch button on every screen. Call once at startup. */
export function initThemePicker(): void {
  document.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('.theme-picker-btn')) open();
  });
}
